/**
 * Live quote subscription hook.
 *
 * Opens a single websocket to the FastAPI /ws/quotes endpoint, sends
 * subscribe/unsubscribe messages as the active symbol changes, and exposes
 * the latest merged quote dict to the component.
 *
 * The websocket is auto-reconnected with exponential backoff if it drops.
 * Before any live tick arrives, we hit the snapshot REST endpoint
 * /api/trade/quote so the UI has something to render immediately.
 *
 * Server message shape (see app/services/quote_stream.py):
 *   { symbol, price, last, bid, ask, bidSize, askSize,
 *     quoteTimestamp, tradeTimestamp }
 *
 * Snapshot REST shape (see app/routers/trade.py):
 *   { success, quote: { symbol, price, bid, ask, last,
 *     previousClose, change, changePercent } }
 */

import { useEffect, useRef, useState } from 'react';

export interface Quote {
  symbol: string;
  price?: number | null;
  last?: number | null;
  bid?: number | null;
  ask?: number | null;
  bidSize?: number | null;
  askSize?: number | null;
  previousClose?: number | null;
  change?: number | null;
  changePercent?: number | null;
  quoteTimestamp?: string | null;
  tradeTimestamp?: string | null;
}

export type StreamStatus = 'idle' | 'connecting' | 'open' | 'closed' | 'error';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const WS_BASE = API_BASE.replace(/^http/, 'ws');

interface UseQuoteStreamResult {
  quote: Quote | null;
  status: StreamStatus;
  error: string | null;
}

export function useQuoteStream(symbol: string | null): UseQuoteStreamResult {
  const [quote, setQuote] = useState<Quote | null>(null);
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [error, setError] = useState<string | null>(null);

  // Refs survive re-renders without forcing them.
  const wsRef = useRef<WebSocket | null>(null);
  const subscribedSymbolRef = useRef<string | null>(null);
  const reconnectTimerRef = useRef<number | null>(null);
  const reconnectAttemptsRef = useRef(0);
  const closedByUserRef = useRef(false);

  // ---- snapshot fallback (so we render before the first tick) -----------
  useEffect(() => {
    if (!symbol) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    const token = localStorage.getItem('authToken');
    if (!token) return;

    fetch(`${API_BASE}/api/trade/quote?symbol=${encodeURIComponent(symbol)}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (cancelled || !data?.quote) return;
        // Only seed if we don't already have a live quote with the same symbol.
        setQuote((prev) =>
          prev && prev.symbol === data.quote.symbol ? prev : data.quote,
        );
      })
      .catch(() => {
        /* ignore — websocket will deliver real data shortly */
      });
    return () => {
      cancelled = true;
    };
  }, [symbol]);

  // ---- websocket lifecycle ---------------------------------------------
  useEffect(() => {
    closedByUserRef.current = false;

    function connect() {
      const token = localStorage.getItem('authToken');
      if (!token) {
        setStatus('error');
        setError('not authenticated');
        return;
      }
      setStatus('connecting');
      const ws = new WebSocket(`${WS_BASE}/ws/quotes?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;

      ws.onopen = () => {
        setStatus('open');
        setError(null);
        reconnectAttemptsRef.current = 0;
        // Re-subscribe to whatever symbol we currently care about.
        if (subscribedSymbolRef.current) {
          ws.send(
            JSON.stringify({
              action: 'subscribe',
              symbol: subscribedSymbolRef.current,
            }),
          );
        }
      };

      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data) as Quote;
          // Ignore stale ticks for symbols we no longer care about.
          if (
            subscribedSymbolRef.current &&
            data.symbol &&
            data.symbol.toUpperCase() !== subscribedSymbolRef.current.toUpperCase()
          ) {
            return;
          }
          setQuote((prev) => ({ ...(prev || {}), ...data }));
        } catch {
          /* ignore malformed message */
        }
      };

      ws.onerror = () => {
        setStatus('error');
        setError('websocket error');
      };

      ws.onclose = () => {
        wsRef.current = null;
        setStatus('closed');
        if (closedByUserRef.current) return;
        // Reconnect with capped exponential backoff.
        const attempt = ++reconnectAttemptsRef.current;
        const delay = Math.min(30_000, 500 * 2 ** Math.min(attempt, 6));
        reconnectTimerRef.current = window.setTimeout(connect, delay);
      };
    }

    connect();

    return () => {
      closedByUserRef.current = true;
      if (reconnectTimerRef.current !== null) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (wsRef.current) {
        try {
          wsRef.current.send(JSON.stringify({ action: 'unsubscribe' }));
        } catch {
          /* ignore */
        }
        wsRef.current.close();
        wsRef.current = null;
      }
    };
  }, []); // open exactly once per mount

  // ---- subscription updates --------------------------------------------
  useEffect(() => {
    const ws = wsRef.current;
    const previous = subscribedSymbolRef.current;
    const next = symbol ? symbol.trim().toUpperCase() : null;

    if (previous === next) return;

    // Tell server to drop the old symbol; we don't want phantom ticks.
    if (ws && ws.readyState === WebSocket.OPEN && previous) {
      ws.send(JSON.stringify({ action: 'unsubscribe', symbol: previous }));
    }
    subscribedSymbolRef.current = next;

    if (ws && ws.readyState === WebSocket.OPEN && next) {
      ws.send(JSON.stringify({ action: 'subscribe', symbol: next }));
    }
    if (!next) {
      setQuote(null);
    }
  }, [symbol]);

  return { quote, status, error };
}
